import { FC, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useDrawer } from "@/components/ui/v2/use-drawer";
import VacancyDetailsDrawer from "@/features/vacancies/components/details/VacancyDetailsDrawer";
import OverviewVacanciesPage from "@/features/vacancies/pages/overview/OverviewVacanciesPage";
import useToast from "@/lib/hooks/useToast";
import { useVacancyDetail } from "@/lib/hooks/useVacancyDetail";
import { VacancyType } from "@/types/vacancy/VacancyType";

type VacancyDetailPageContentProps = {
  clientId?: string;
  vacancyId: string;
};
export const VacancyDetailPageContent: FC<VacancyDetailPageContentProps> = ({
  clientId,
  vacancyId,
}) => {
  const [searchParams] = useSearchParams();
  const { closeDrawer, isClosing, openDrawer } = useDrawer();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const { vacancy, vacancyError, vacancyFetching, vacancyLoading } =
    useVacancyDetail();
  const navigate = useNavigate();

  const openVacancyDrawer = useCallback(
    (
      vacancy: VacancyType | undefined,
      options?: { isLoading?: boolean; isFetching?: boolean }
    ) => {
      const basePath = clientId
        ? `/clients/${clientId}/vacancies`
        : "/vacancies";
      const queryString = searchParams?.toString();
      const to = `${basePath}${queryString ? `?${queryString}` : ""}`;

      openDrawer(
        <VacancyDetailsDrawer
          clientId={clientId}
          isFetching={options?.isFetching ?? false}
          isLoading={options?.isLoading ?? false}
          vacancy={vacancy}
          onNotFound={() => {
            closeDrawer();
            navigate(to, { replace: true });
          }}
        />,
        "Vacancy details",
        "medium",
        () => {
          if (vacancyId) {
            navigate(to, { replace: true });
          }
        }
      );
    },
    [clientId, closeDrawer, navigate, openDrawer, searchParams, vacancyId]
  );

  useEffect(() => {
    if (vacancyError) {
      showToast(
        t("errors.invalidEntity", { entity: t("common.vacancy") }),
        "error"
      );
    }

    if (vacancyId && !isClosing) {
      openVacancyDrawer(vacancy, {
        isLoading: vacancyLoading,
        isFetching: vacancyFetching,
      });
    }
  }, [
    isClosing,
    openVacancyDrawer,
    vacancy,
    vacancyFetching,
    vacancyId,
    vacancyLoading,
    vacancyError,
    showToast,
    t,
  ]);

  useEffect(() => {
    return () => {
      closeDrawer();
    };
  }, [closeDrawer]);

  return <OverviewVacanciesPage />;
};
